// Edit one button's settings — shared by the mobile and desktop shells.
//
// Ports the fields of button_editing_form.dart that live on OutputDetails and
// share one save path:
//
//   • name
//   • All On behaviour   (+ minutes when "Schedule Time")
//   • All Off behaviour  (+ minutes when "Schedule Time")
//   • Key Press Off After
//   • Button State (power-restore)
//
// Two Way Mapping opens from here but SAVES SEPARATELY — it lives in its own
// table, not on this row, so it is not part of this dialog's Save. Schedule
// keeps its own screen and is linked from the actions.
//
// ⚠️ Saving writes the row AND pushes the firmware registers — see
// core/devices/buttonSettings. A row-only write shows the new setting in the
// app while the switch keeps the old behaviour.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, Typography, Box, CircularProgress, Alert,
} from '@mui/material';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { useApp } from '../../core/state/AppContext';
import TwoWayMappingDialog from './TwoWayMappingDialog';
import {
  ALL_ON_OPTIONS, ALL_OFF_OPTIONS, BUTTON_STATE_OPTIONS, SCHEDULE_TIME_OPTION,
  packAllOnOff, unpackAllOnOff, getOutputRow, saveButtonSettings,
} from '../../core/devices/buttonSettings';

const num = (v) => (Number(v) || 0);

export default function ButtonSettingsDialog({
  open, uniqueNumber, output, onClose, onSaved, onOpenSchedule,
}) {
  const { userId } = useApp();
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [twoWayOpen, setTwoWayOpen] = useState(false);

  const [name, setName] = useState('');
  const [allOn, setAllOn] = useState(0);
  const [allOff, setAllOff] = useState(0);
  const [afterAllOn, setAfterAllOn] = useState(0);
  const [afterAllOff, setAfterAllOff] = useState(0);
  const [keyPressOff, setKeyPressOff] = useState(0);
  const [state, setState] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getOutputRow(uniqueNumber, output);
      const r = (Array.isArray(res?.data) ? res.data[0] : null) || {};
      setRow(r);
      setName(r.OutputName ?? '');
      const { allOn: on, allOff: off } = unpackAllOnOff(r.on_all_on_status);
      setAllOn(on);
      setAllOff(off);
      setAfterAllOn(num(r.OnTimeAfterAllOn));
      setAfterAllOff(num(r.OnTimeAfterAllOff));
      setKeyPressOff(num(r.OnTimeAfterOn));
      setState(Math.min(2, Math.max(0, num(r.PowerOnAction))));
    } catch (e) {
      setError('Could not load this button’s settings.');
    } finally {
      setLoading(false);
    }
  }, [uniqueNumber, output]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const submit = async () => {
    setSaving(true);
    setError('');
    const changes = {
      OutputName: name.trim(),
      on_all_on_status: packAllOnOff(allOn, allOff),
      // Only meaningful for "Schedule Time", but sent regardless so switching
      // away from it does not leave a stale delay behind in the register.
      OnTimeAfterAllOn: allOn === SCHEDULE_TIME_OPTION ? num(afterAllOn) : 0,
      OnTimeAfterAllOff: allOff === SCHEDULE_TIME_OPTION ? num(afterAllOff) : 0,
      OnTimeAfterOn: num(keyPressOff),
      PowerOnAction: state,
    };
    const res = await saveButtonSettings(uniqueNumber, output, changes, row, userId);
    setSaving(false);
    if (!res.ok) { setError(res.error); return; }
    if (onSaved) onSaved();
    onClose();
  };

  const select = (label, value, onChange, options, help) => (
    <Box sx={{ mt: 2 }}>
      <TextField
        select fullWidth size="small" label={label} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      >
        {options.map((o, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <MenuItem key={i} value={i}>{o}</MenuItem>
        ))}
      </TextField>
      {help && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {help}
        </Typography>
      )}
    </Box>
  );

  const minutes = (label, value, onChange) => (
    <TextField
      size="small" type="number" label={label} value={value}
      onChange={(e) => onChange(e.target.value)}
      inputProps={{ min: 0 }}
      sx={{ mt: 1, width: 180 }}
    />
  );

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{`Button Settings — ${output}`}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading…</Typography>
          </Box>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

            <TextField
              fullWidth size="small" label="Button name" value={name}
              onChange={(e) => setName(e.target.value)}
            />

            {select('All On', allOn, setAllOn, ALL_ON_OPTIONS,
              'What this button does when the All On bar is pressed.')}
            {allOn === SCHEDULE_TIME_OPTION
              && minutes('Turn on for (minutes)', afterAllOn, setAfterAllOn)}

            {select('All Off', allOff, setAllOff, ALL_OFF_OPTIONS,
              'What this button does when the All Off bar is pressed.')}
            {allOff === SCHEDULE_TIME_OPTION
              && minutes('Turn off after (minutes)', afterAllOff, setAfterAllOff)}

            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth size="small" type="number" label="Key press off after (minutes)"
                value={keyPressOff}
                onChange={(e) => setKeyPressOff(e.target.value)}
                inputProps={{ min: 0 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Switches itself off this many minutes after a press. 0 disables it.
              </Typography>
            </Box>

            {select('Button state', state, setState, BUTTON_STATE_OPTIONS,
              'What happens when power comes back. "For Router" waits a minute, '
              + 'then cycles the output so a router restarts cleanly.')}

            {/* Two-way lives in its own table, not on this row, so it saves
                independently of the fields above rather than on Save. */}
            <Button
              startIcon={<SyncAltIcon />}
              onClick={() => setTwoWayOpen(true)}
              sx={{ mt: 2, textTransform: 'none' }}
            >
              Two Way Mapping…
            </Button>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {/* Schedules keep their own screen — this dialog is the way in, so the
            entry point long-press used to provide is not lost. */}
        {onOpenSchedule && (
          <Button onClick={onOpenSchedule} disabled={saving} sx={{ mr: 'auto' }}>
            Schedules
          </Button>
        )}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={saving || loading}>
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>

      <TwoWayMappingDialog
        open={twoWayOpen}
        uniqueNumber={uniqueNumber}
        output={output}
        outputName={name}
        onClose={() => setTwoWayOpen(false)}
      />
    </Dialog>
  );
}
